import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Table,
  TableBody,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Edit from '@mui/icons-material/Edit';
import AddressDialog from 'src/components/AddressDialog/AddressDialog';
import AddressDetailBox from 'src/components/AddressDetailBox/AddressDetailBox';
import {
  loadClientAvalsList,
  updateAval
} from 'src/store/actions/clientActions';
import useStyles from '../styles';

const AvalAddress = ({ aval, isDisabledEdit }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [avalAddressOptions, setAvalAddressOptions] = useState([]);

  const handleClickOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleSubmit = useCallback(
    values => {
      dispatch(updateAval(aval.id, values, () => handleClose()));
    },
    [aval, dispatch]
  );

  const setAvalsAddressOptions = useCallback(
    avalList => {
      setAvalAddressOptions(
        avalList
          .filter(avalOption => Boolean(avalOption.address.street))
          .map(avalOption => {
            return {
              fullName: avalOption.fullName,
              street: avalOption.address.street,
              externalHomeNumber: avalOption.address.externalHomeNumber,
              insideHomeNumber: avalOption.address.insideHomeNumber || '',
              zipCode: avalOption.address.zipCode,
              city: avalOption.address.city,
              colonia: avalOption.address.colonia,
              municipality: avalOption.address.municipality || '',
              state: avalOption.address.state
            };
          })
      );
    },
    [avalAddressOptions]
  );

  useEffect(() => {
    if (open) {
      dispatch(loadClientAvalsList(aval.clientId, setAvalsAddressOptions));
    }
  }, [open]);

  return (
    <Card variant="elevation">
      <CardHeader
        className={classes.header}
        title={
          <Typography className={classes.tableCell} component="h4" variant="h4">
            {t('CLIENTS_VIEW.AVAL_DETAIL.AVAL_ADDRESS')}
          </Typography>
        }
      />
      <Divider className={classes.tableCell} />
      <CardContent style={{ padding: 0, marginBottom: '16px' }}>
        <Table>
          <TableBody>
            <AddressDetailBox address={aval.address} includeCountry includeMunicipality />
          </TableBody>
        </Table>
        <AddressDialog
          open={open}
          address={aval.address}
          handleClose={handleClose}
          submit={handleSubmit}
          existingAddressOptions={avalAddressOptions}
          useExistingAddress={Boolean(avalAddressOptions.length)}
          includeCountry
          includeMunicipality
        />

        <Grid container direction="column" alignItems="stretch">
          <Grid item className={classes.buttons}>
            <Button
              onClick={handleClickOpen}
              disabled={isDisabledEdit}
              variant="contained"
              color="primary"
              className={clsx(classes.label, classes.buttons)}
              startIcon={<Edit />}
            >
              {t('COMMON.EDIT')}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

AvalAddress.propTypes = {
  aval: PropTypes.object,
  isDisabledEdit: PropTypes.bool
};

export default AvalAddress;
